/**
 * THE WIRE.
 *
 * Everything two machines say to each other is defined here and nowhere else, so
 * that "what does a snapshot contain" has exactly one answer and both ends read
 * it from the same file.
 *
 * Two encodings, chosen per message rather than globally:
 *
 * BINARY for the hot path. Commands go up thirty times a second and snapshots
 * come down twenty, forever, for the whole round. A JSON snapshot of eight
 * players and three guards is about 1.2kB; the binary one below is 158 bytes.
 * That is not an optimisation for its own sake — the target transport is Steam's
 * relay network, where a peer on a bad hotel connection is the constraint, and
 * the difference between 3kB/s and 24kB/s per client is the difference between a
 * round that survives that peer and one that does not.
 *
 * JSON for everything else. Roster changes, round events, chat: rare, structural,
 * and much more likely to gain a field next week than to gain a player. Paying
 * four bytes of tag for something that happens twice a minute buys nothing, and
 * a hand-rolled codec for a message shape that keeps changing is how protocols
 * end up with silent version skew.
 *
 * PLAYERS ARE SLOTS ON THE WIRE. A peer is a string id everywhere in the game and
 * a number 0..15 in a snapshot. The mapping is published in the roster, which is
 * reliable and ordered, so by the time a snapshot mentions slot 3 the client
 * already knows who that is. This keeps a player record at fourteen bytes instead
 * of forty, and it makes the per-frame path allocate nothing.
 */

/** Fixed simulation step. Both ends run the character controller at this rate. */
export const TICK_HZ = 60;
export const TICK_DT = 1 / TICK_HZ;
/** Snapshots per second. Every third tick. */
export const SNAPSHOT_HZ = 20;
export const TICKS_PER_SNAPSHOT = Math.round(TICK_HZ / SNAPSHOT_HZ);
/** Hard ceiling on lobby size. The slot byte and the roster UI both assume it. */
export const MAX_PLAYERS = 16;

export const enum Msg {
  /** client -> host, once, on connect. */
  Hello = 1,
  /** host -> one client, in reply. */
  Welcome = 2,
  /** host -> all, whenever the lobby changes. */
  Roster = 3,
  /** client -> host, every network tick, carrying several sim ticks. */
  Commands = 4,
  /** host -> all, twenty times a second. */
  Snapshot = 5,
  /** host -> all: something happened that the HUD or the mix must react to. */
  Event = 6,
  /** client -> host: an edge-triggered action, with the target it was aimed at. */
  Act = 7,
  /** either direction: encoded voice, relayed by the host. */
  Voice = 8,
  /** client -> host and back, for the clock estimate. */
  Ping = 9,
  Pong = 10,
  /** client -> host: lobby choices — team, name, ready. */
  Lobby = 11,
}

// ---------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------

/** One simulation tick of intent from one player. */
export interface Command {
  /** Monotonic per client. Wraps at 65536, which is eighteen minutes at 60Hz. */
  seq: number;
  /** -1, 0 or 1. */
  forward: number;
  strafe: number;
  /** Bit field, see `Btn`. */
  buttons: number;
  /** Absolute, radians. The client owns its own aim; sending deltas drifts. */
  yaw: number;
  pitch: number;
}

export const enum Btn {
  Jump = 1,
  Sprint = 2,
  Crouch = 4,
  Interact = 8,
}

export function makeCommand(): Command {
  return { seq: 0, forward: 0, strafe: 0, buttons: 0, yaw: 0, pitch: 0 };
}

/** Bytes per command record on the wire. */
const CMD_BYTES = 7;

/**
 * A batch of commands, oldest first.
 *
 * Sent as a batch rather than one per packet because the send rate is lower than
 * the tick rate and because it makes a dropped packet nearly free: the next one
 * re-sends everything the host has not acknowledged, so a single loss costs
 * latency rather than a swallowed jump.
 */
export function encodeCommands(cmds: readonly Command[]): ArrayBuffer {
  const n = Math.min(cmds.length, 255);
  const buf = new ArrayBuffer(2 + n * CMD_BYTES);
  const v = new DataView(buf);
  v.setUint8(0, Msg.Commands);
  v.setUint8(1, n);
  let o = 2;
  for (let i = cmds.length - n; i < cmds.length; i++) {
    const c = cmds[i];
    v.setUint16(o, c.seq & 0xffff);
    // Two axes in one byte. Each is -1, 0 or 1, so two bits each with a +1 bias.
    v.setUint8(o + 2, (c.forward + 1) | ((c.strafe + 1) << 2));
    v.setUint8(o + 3, c.buttons & 0xff);
    v.setInt16(o + 4, quantAngle(c.yaw));
    v.setInt8(o + 6, quantPitch(c.pitch));
    o += CMD_BYTES;
  }
  return buf;
}

export function decodeCommands(buf: ArrayBuffer, out: Command[] = []): Command[] {
  const v = new DataView(buf);
  const n = v.getUint8(1);
  out.length = 0;
  let o = 2;
  for (let i = 0; i < n; i++) {
    const axes = v.getUint8(o + 2);
    out.push({
      seq: v.getUint16(o),
      forward: (axes & 0x3) - 1,
      strafe: ((axes >> 2) & 0x3) - 1,
      buttons: v.getUint8(o + 3),
      yaw: unquantAngle(v.getInt16(o + 4)),
      pitch: unquantPitch(v.getInt8(o + 6)),
    });
    o += CMD_BYTES;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Snapshots
// ---------------------------------------------------------------------------

/** What the host says about one player, twenty times a second. */
export interface NetPlayer {
  slot: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
  /** See `PFlag`. */
  flags: number;
  /** Sim `PlayerStatus` as an index: 0 active, 1 downed, 2 caught, 3 escaped. */
  status: number;
  /** The last command sequence the host has consumed from this player. */
  ack: number;
  /** Carried mass in kilograms, rounded up to 0.25kg. Drives remote gait. */
  kg: number;
}

/** What the host says about one guard. */
export interface NetGuard {
  slot: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  /** Index into the guard mode table, plus alertness in the top nibble. */
  mode: number;
  alert: number;
}

export const enum PFlag {
  Crouching = 1,
  Sprinting = 2,
  Grounded = 4,
  /** Mid-interaction. Remote avatars play the work pose and the loop. */
  Working = 8,
  /** On the guard team. */
  Guard = 16,
}

export interface Snapshot {
  /** Host tick. Wraps at 2^32, which is two years. */
  tick: number;
  /** Host clock in milliseconds since its own boot. The interpolation timebase. */
  time: number;
  /** Seconds left in the round, quarter-second resolution. */
  clock: number;
  /** 0 briefing, 1 active, 2 results. */
  phase: number;
  players: NetPlayer[];
  guards: NetGuard[];
}

const P_BYTES = 15;
const G_BYTES = 12;

export function encodeSnapshot(s: Snapshot): ArrayBuffer {
  const buf = new ArrayBuffer(14 + s.players.length * P_BYTES + s.guards.length * G_BYTES);
  const v = new DataView(buf);
  v.setUint8(0, Msg.Snapshot);
  v.setUint8(1, s.phase);
  v.setUint32(2, s.tick >>> 0);
  v.setUint32(6, s.time >>> 0);
  v.setUint16(10, Math.max(0, Math.min(65535, Math.round(s.clock * 4))));
  v.setUint8(12, s.players.length);
  v.setUint8(13, s.guards.length);
  let o = 14;
  for (const p of s.players) {
    v.setUint8(o, p.slot);
    v.setInt16(o + 1, quantPos(p.x));
    v.setInt16(o + 3, quantPos(p.y));
    v.setInt16(o + 5, quantPos(p.z));
    v.setInt16(o + 7, quantAngle(p.yaw));
    v.setInt8(o + 9, quantPitch(p.pitch));
    v.setUint8(o + 10, p.flags | (p.status << 5));
    v.setUint16(o + 11, p.ack & 0xffff);
    v.setUint8(o + 13, Math.min(255, Math.round(p.kg * 4)));
    v.setUint8(o + 14, 0);
    o += P_BYTES;
  }
  for (const g of s.guards) {
    v.setUint8(o, g.slot);
    v.setInt16(o + 1, quantPos(g.x));
    v.setInt16(o + 3, quantPos(g.y));
    v.setInt16(o + 5, quantPos(g.z));
    v.setInt16(o + 7, quantAngle(g.yaw));
    v.setUint8(o + 9, g.mode);
    v.setUint8(o + 10, Math.round(Math.max(0, Math.min(1, g.alert)) * 255));
    v.setUint8(o + 11, 0);
    o += G_BYTES;
  }
  return buf;
}

export function decodeSnapshot(buf: ArrayBuffer): Snapshot {
  const v = new DataView(buf);
  const np = v.getUint8(12);
  const ng = v.getUint8(13);
  const players: NetPlayer[] = [];
  const guards: NetGuard[] = [];
  let o = 14;
  for (let i = 0; i < np; i++) {
    const packed = v.getUint8(o + 10);
    players.push({
      slot: v.getUint8(o),
      x: unquantPos(v.getInt16(o + 1)),
      y: unquantPos(v.getInt16(o + 3)),
      z: unquantPos(v.getInt16(o + 5)),
      yaw: unquantAngle(v.getInt16(o + 7)),
      pitch: unquantPitch(v.getInt8(o + 9)),
      flags: packed & 0x1f,
      status: packed >> 5,
      ack: v.getUint16(o + 11),
      kg: v.getUint8(o + 13) / 4,
    });
    o += P_BYTES;
  }
  for (let i = 0; i < ng; i++) {
    guards.push({
      slot: v.getUint8(o),
      x: unquantPos(v.getInt16(o + 1)),
      y: unquantPos(v.getInt16(o + 3)),
      z: unquantPos(v.getInt16(o + 5)),
      yaw: unquantAngle(v.getInt16(o + 7)),
      mode: v.getUint8(o + 9),
      alert: v.getUint8(o + 10) / 255,
    });
    o += G_BYTES;
  }
  return {
    tick: v.getUint32(2),
    time: v.getUint32(6),
    clock: v.getUint16(10) / 4,
    phase: v.getUint8(1),
    players,
    guards,
  };
}

// ---------------------------------------------------------------------------
// Control messages — JSON, because they are rare and they will keep changing
// ---------------------------------------------------------------------------

export type Team = 'thief' | 'guard';

export interface RosterEntry {
  id: string;
  slot: number;
  name: string;
  team: Team;
  ready: boolean;
  /** Round-trip time in milliseconds as the host last measured it. */
  ping: number;
  /** True for the one peer running the simulation. */
  host: boolean;
}

export type Control =
  | { t: 'hello'; name: string; version: number }
  | { t: 'welcome'; you: string; slot: number; hostTime: number; roster: RosterEntry[]; phase: number }
  | { t: 'roster'; roster: RosterEntry[] }
  | { t: 'lobby'; name?: string; team?: Team; ready?: boolean; start?: boolean }
  | { t: 'act'; action: string; target?: string }
  | { t: 'event'; e: NetEvent }
  | { t: 'ping'; at: number }
  | { t: 'pong'; at: number; hostTime: number };

/**
 * Something the host did that a client cannot work out for itself.
 *
 * Deliberately not "the round event type from the sim". The sim's events are an
 * internal record of everything that happened; this is the subset a remote
 * machine needs in order to draw and sound the same, and keeping them separate
 * means the sim can gain an event without silently changing the protocol.
 */
export type NetEvent =
  /**
   * A line of HUD text. `for` addresses exactly one player; `not` addresses
   * everybody except one — which is what "Sam is out" wants, since Sam already
   * got the better version of that news the moment they touched the van.
   */
  | { t: 'toast'; text: string; tone: 'good' | 'bad' | 'info'; for?: string; not?: string }
  | { t: 'breach'; id: string; by: string; noise: number }
  | { t: 'looted'; container: string; item: string; by: string; cleared: boolean }
  | { t: 'banked'; by: string; amount: number; items: number; at: [number, number, number] }
  | { t: 'loose'; id: string; item: string; at: [number, number, number] }
  | { t: 'took'; id: string }
  | { t: 'capture'; who: string; at: [number, number, number] }
  | { t: 'guardDown'; guard: string; at: [number, number, number] }
  | { t: 'phase'; phase: number }
  /**
   * A new night. The seed rides along because every client deals its own
   * containers and starts its own guards from it — without it two machines would
   * disagree about which case the diamonds are in, which is the worst possible
   * class of desync: silent, and only discovered by a player mid-grind.
   */
  | { t: 'reset'; seed: number }
  | { t: 'radio'; line: string; at: [number, number, number] }
  /**
   * `floor` is what was still in the building at the bell. Sent rather than
   * derived for the same reason the rows are: a client's container map is built
   * from a stream of loot events, and the one screen everybody screenshots is
   * the worst possible place for a dropped event to first become visible.
   */
  | { t: 'score'; rows: NetScoreRow[]; reason: string; floor: number }
  /** Somebody else's inventory changed enough that the HUD must be told. */
  | { t: 'inv'; who: string; carried: string[]; tools: string[]; banked: number };

/**
 * One line of the scoreboard, sent rather than derived.
 *
 * A client could almost compute this from its own copy of the round, and that is
 * exactly the trap: "almost" means the results screen — the one screen everybody
 * stares at and screenshots — would be the place a divergence finally showed up.
 * It costs sixty bytes a player, once.
 */
export interface NetScoreRow {
  id: string;
  name: string;
  team: Team;
  banked: number;
  unbanked: number;
  status: string;
}

export function encodeControl(c: Control): string {
  return JSON.stringify(c);
}

export function decodeControl(s: string): Control | null {
  try {
    const v = JSON.parse(s) as Control;
    return v && typeof v.t === 'string' ? v : null;
  } catch {
    return null;
  }
}

/** The kind byte of a binary message, or 0 if it is text. */
export function kindOf(data: ArrayBuffer | string): number {
  return typeof data === 'string' ? 0 : new DataView(data).getUint8(0);
}

// ---------------------------------------------------------------------------
// Voice
// ---------------------------------------------------------------------------

/** Kind, flags, a 16-bit sequence, a speaker slot. The codec payload follows. */
const VOICE_HEADER = 5;

export const enum VoiceFlag {
  /** Carried by a walkie-talkie rather than by air: no distance, but filtered. */
  Radio = 1,
}

/**
 * "The person who sent you this is the person who said it."
 *
 * True of every packet on its first hop and false of every packet on its
 * second, which is the whole reason this byte exists — see `stampVoice`.
 */
export const VOICE_SELF = 0xff;

/**
 * One packet of encoded speech.
 *
 * Deliberately not a `Snapshot`-style struct. Voice is the one thing on this
 * wire that is genuinely opaque — the payload is whatever the codec produced,
 * and nothing between the two microphones is allowed to have an opinion about
 * it. All this layer adds is who to play it through and in what order.
 *
 * The sequence number is here rather than left to the codec because packets
 * arrive out of order over any real network and the receiver has to be able to
 * tell a late frame from a new one. Sixteen bits at fifty frames a second wraps
 * every twenty-two minutes, which `seqNewer` already handles.
 */
export function encodeVoice(seq: number, payload: Uint8Array, flags = 0): ArrayBuffer {
  const buf = new ArrayBuffer(VOICE_HEADER + payload.byteLength);
  const v = new DataView(buf);
  v.setUint8(0, Msg.Voice);
  v.setUint8(1, flags);
  v.setUint16(2, seq & 0xffff, true);
  v.setUint8(4, VOICE_SELF);
  new Uint8Array(buf, VOICE_HEADER).set(payload);
  return buf;
}

/**
 * Write the speaker's slot into a frame the host is about to pass on.
 *
 * EVERY VOICE IS RELAYED, SO EVERY VOICE ARRIVES FROM THE HOST.
 *
 * A receiver only knows which socket a packet came down, and for three players
 * out of four that socket is the host's. Without this byte the mall puts every
 * word anybody says in the host's mouth, at the host's position — which sounds
 * like a bug in the panner and is in fact a bug in the topology. The slot is
 * used rather than the id because it is one byte instead of eight or more, on
 * the one message that goes out fifty times a second per talking player.
 */
export function stampVoice(data: ArrayBuffer, slot: number): ArrayBuffer {
  const out = data.slice(0);
  if (out.byteLength > VOICE_HEADER) new DataView(out).setUint8(4, slot & 0xff);
  return out;
}

export function decodeVoice(
  data: ArrayBuffer,
): { seq: number; flags: number; slot: number; payload: Uint8Array } | null {
  if (data.byteLength <= VOICE_HEADER) return null;
  const v = new DataView(data);
  if (v.getUint8(0) !== Msg.Voice) return null;
  return {
    flags: v.getUint8(1),
    seq: v.getUint16(2, true),
    /** `VOICE_SELF` unless a host relayed it. See `stampVoice`. */
    slot: v.getUint8(4),
    // A view, not a copy. The decoder consumes it synchronously and a voice
    // frame arrives fifty times a second per speaker.
    payload: new Uint8Array(data, VOICE_HEADER),
  };
}

// ---------------------------------------------------------------------------
// Quantisation
// ---------------------------------------------------------------------------

/**
 * Positions to the centimetre.
 *
 * A signed 16-bit centimetre covers ±327 metres, and the mall's furthest corner
 * is 120 metres from the origin, so there is more than a factor of two in hand.
 * A centimetre is well under the width of the capsule, so a remote body never
 * visibly quantises — and this is not the authority anyway. The client's own
 * position comes from its own controller; this is what it is corrected against.
 */
const POS_SCALE = 100;
function quantPos(v: number): number {
  return Math.max(-32768, Math.min(32767, Math.round(v * POS_SCALE)));
}
function unquantPos(v: number): number {
  return v / POS_SCALE;
}

/** A full turn in 65536 steps: a fifth of a milliradian. */
function quantAngle(a: number): number {
  let x = a % (Math.PI * 2);
  if (x > Math.PI) x -= Math.PI * 2;
  if (x < -Math.PI) x += Math.PI * 2;
  return Math.max(-32768, Math.min(32767, Math.round((x / Math.PI) * 32767)));
}
function unquantAngle(v: number): number {
  return (v / 32767) * Math.PI;
}

/** Pitch is clamped to ±90° anyway, so a byte is 0.7° — below the noise floor of a head. */
function quantPitch(p: number): number {
  return Math.max(-127, Math.min(127, Math.round((p / (Math.PI / 2)) * 127)));
}
function unquantPitch(v: number): number {
  return (v / 127) * (Math.PI / 2);
}

export const _internals = { quantPos, unquantPos, quantAngle, unquantAngle, quantPitch, unquantPitch };
